import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ApiError, loadOfficeFile, saveOfficeFile, type OfficeEditorKind } from "@/lib/api";
import { shouldAutosave } from "@/lib/officeAutosave";
import { getTheme } from "@/lib/theme";

const LABEL: Record<OfficeEditorKind, string> = {
  gridcraft: "GridCraft",
  wordcraft: "WordCraft",
  deckcraft: "DeckCraft",
};
const EXTENSION: Record<OfficeEditorKind, string> = {
  gridcraft: "xlsx",
  wordcraft: "docx",
  deckcraft: "pptx",
};
const MAX_BYTES = 50 * 1024 * 1024; // Matches the docs upload limit.
const AUTOSAVE_CHECK_MS = 1000;
const EXPORT_TIMEOUT_MS = 15000;
const LOAD_TIMEOUT_MS = 45000;

function downloadCopy(bytes: ArrayBuffer, name: string, type: string) {
  const url = URL.createObjectURL(new Blob([bytes], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Full-screen office editor: a pinned, self-hosted WASM app in a same-origin
 * iframe (see frontend/vendor). Only this parent talks to the API. The editor's
 * own "saved" state means nothing here — a change counts as saved only once the
 * server has acknowledged it.
 */
export function OfficeEditorModal({
  editor,
  docId,
  name,
  mime,
  onClose,
  onSaved,
}: {
  editor: OfficeEditorKind;
  docId: string;
  name: string;
  mime: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const frame = useRef<HTMLIFrameElement>(null);
  // Fixed at open: this full-screen editor covers the theme toggle.
  const [initialTheme] = useState(getTheme);
  const [epoch, setEpoch] = useState(0);
  const [status, setStatus] = useState("Loading…");
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [hasCopy, setHasCopy] = useState(false);
  const [closing, setClosing] = useState(false);
  const flushRef = useRef<() => Promise<boolean>>(async () => true);
  const retryRef = useRef<() => void>(() => undefined);
  const copyRef = useRef<() => void>(() => undefined);
  const savedAnything = useRef(false);

  const post = (message: object) =>
    frame.current?.contentWindow?.postMessage(message, window.location.origin);
  const sendTheme = () => post({ type: "vernias:theme", theme: getTheme() });

  useEffect(() => {
    let alive = true;
    let ready = false;
    let opened = false;
    let editorLoaded = false;
    let revision: string | undefined;
    let bytes: ArrayBuffer | undefined;
    let latest: ArrayBuffer | undefined;
    // Bumped on every edit; a save only clears "dirty" if no edit came after
    // the export it carries.
    let generation = 0;
    let dirty = false;
    let exportPending = false;
    let exportGeneration = 0;
    let exportTimer: ReturnType<typeof setTimeout> | undefined;
    let running = false;
    let failed = false;
    let changedElsewhere = false;
    let cellEditing = false;
    let lastInputAt = 0;
    let dirtySince = 0;
    const queue: { bytes: ArrayBuffer; generation: number }[] = [];
    const waiters: ((saved: boolean) => void)[] = [];
    const settle = (saved: boolean) => waiters.splice(0).forEach((resolve) => resolve(saved));
    const markDirty = () => {
      generation++;
      if (!dirty) dirtySince = Date.now();
      lastInputAt = Date.now();
      dirty = true;
      setStatus("Unsaved changes");
    };

    const requestExport = () => {
      if (!opened || exportPending || changedElsewhere) return;
      exportPending = true;
      exportGeneration = generation;
      post({ type: "vernias:save" });
      exportTimer = setTimeout(() => {
        exportPending = false;
        failed = true;
        setError("The editor could not save. Keep this document open and try again.");
        settle(false);
      }, EXPORT_TIMEOUT_MS);
    };

    const drainQueue = async () => {
      if (running || !revision || !alive) return;
      running = true;
      try {
        while (queue.length && alive) {
          const job = queue[0];
          setStatus("Saving…");
          setError(null);
          failed = false;
          const saved = await saveOfficeFile(docId, job.bytes, name, mime, revision);
          revision = saved.revision;
          bytes = job.bytes;
          savedAnything.current = true;
          queue.shift();
          if (!queue.length && generation === job.generation) {
            dirty = false;
            setStatus("All changes saved");
            if (!exportPending) settle(true);
          }
        }
      } catch (e) {
        failed = true;
        settle(false);
        if (!alive) return;
        if (e instanceof ApiError && e.status === 409) {
          changedElsewhere = true;
          setConflict(true);
        } else {
          setError(e instanceof ApiError ? e.message : "Save failed");
        }
        setStatus("Changes not saved");
      } finally {
        running = false;
      }
    };

    flushRef.current = () =>
      new Promise<boolean>((resolve) => {
        if (!editorLoaded || changedElsewhere) return resolve(!dirty);
        if (!dirty && !running && !queue.length && !exportPending) return resolve(true);
        setStatus("Saving…");
        waiters.push(resolve);
        // Capture edits made during an earlier in-flight save too.
        requestExport();
        if (queue.length) void drainQueue();
      });
    retryRef.current = () => {
      failed = false;
      setError(null);
      if (queue.length) void drainQueue();
      else requestExport();
    };
    copyRef.current = () => {
      if (latest) downloadCopy(latest, name, mime);
    };

    const sendOpen = () => {
      if (!alive || !ready || !bytes || opened) return;
      opened = true;
      // Sent as a copy: `bytes` stays usable for the unchanged-export check.
      post({ type: `${editor}:open`, name, bytes: bytes.slice(0) });
    };

    setLoaded(false);
    setStatus("Loading…");
    setError(null);
    setConflict(false);
    setHasCopy(false);
    void loadOfficeFile(docId)
      .then((file) => {
        revision = file.revision;
        bytes = file.bytes;
        sendOpen();
      })
      .catch((e) => {
        if (alive) setError(e instanceof Error ? e.message : "Could not open this file");
      });
    const loadTimeout = setTimeout(() => {
      if (alive && !editorLoaded) setError("The document is taking too long to load. Please reload.");
    }, LOAD_TIMEOUT_MS);
    const autosave = setInterval(() => {
      if (
        dirty &&
        !running &&
        !queue.length &&
        !failed &&
        shouldAutosave({ now: Date.now(), lastInputAt, dirtySince, cellEditing })
      ) {
        requestExport();
      }
    }, AUTOSAVE_CHECK_MS);

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || event.source !== frame.current?.contentWindow) return;
      const message = event.data as { type?: string; dirty?: boolean; editing?: boolean; name?: unknown; bytes?: unknown; saveAs?: boolean };
      if (!message || typeof message !== "object") return;
      if (message.type === "vernias:input") {
        markDirty();
      } else if (message.type === "vernias:cell-edit") {
        cellEditing = editor === "gridcraft" && message.editing === true;
        lastInputAt = Date.now();
      } else if (message.type === "vernias:loaded" && opened) {
        editorLoaded = true;
        clearTimeout(loadTimeout);
        setLoaded(true);
        setStatus("All changes saved");
      } else if (message.type === `${editor}:ready`) {
        ready = true;
        sendTheme();
        sendOpen();
      } else if (message.type === `${editor}:failed`) {
        setError(`${LABEL[editor]} could not open this file.`);
      } else if (message.type === `${editor}:dirty` && message.dirty === true) {
        markDirty();
      } else if (message.type === `${editor}:save` && opened) {
        const automatic = exportPending;
        exportPending = false;
        clearTimeout(exportTimer);
        const raw = message.bytes;
        const buffer =
          raw instanceof ArrayBuffer ? raw : raw instanceof Uint8Array ? raw.slice().buffer : undefined;
        if (!buffer || !buffer.byteLength || buffer.byteLength > MAX_BYTES) {
          failed = true;
          settle(false);
          setError("The exported file is empty or exceeds the 50 MB limit.");
          return;
        }
        const filename = typeof message.name === "string" ? message.name : name;
        // Save As and other-format exports are copies for the user, not saves.
        if (/\.pdf$/i.test(filename)) return downloadCopy(buffer, filename, "application/pdf");
        if (message.saveAs === true || !filename.toLowerCase().endsWith(`.${EXTENSION[editor]}`)) {
          return downloadCopy(buffer, filename, "application/octet-stream");
        }
        const carries = automatic ? exportGeneration : generation;
        const view = new Uint8Array(buffer);
        const unchanged =
          bytes &&
          bytes.byteLength === buffer.byteLength &&
          new Uint8Array(bytes).every((value, i) => value === view[i]);
        if (unchanged && generation === carries && !running && !queue.length) {
          dirty = false;
          setStatus("All changes saved");
          settle(true);
          return;
        }
        latest = buffer;
        setHasCopy(true);
        dirty = true;
        queue.push({ bytes: buffer, generation: carries });
        void drainQueue();
      }
    };
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (dirty || running || queue.length) event.preventDefault();
    };
    window.addEventListener("message", onMessage);
    window.addEventListener("beforeunload", beforeUnload);
    return () => {
      alive = false;
      clearTimeout(loadTimeout);
      clearTimeout(exportTimer);
      clearInterval(autosave);
      settle(false);
      window.removeEventListener("message", onMessage);
      window.removeEventListener("beforeunload", beforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, docId, name, mime, epoch]);

  const close = async () => {
    setClosing(true);
    const saved = await flushRef.current();
    setClosing(false);
    if (!saved && !confirm("Your latest changes could not be saved. Close and lose them?")) return;
    if (savedAnything.current) onSaved();
    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex flex-col bg-bg">
      <header className="flex shrink-0 items-center gap-3 border-b border-border bg-bg-card px-4 py-2">
        <i className="fa-solid fa-pen-to-square text-sm text-accent" />
        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-text-heading">{name}</span>
        <span className="text-xs text-text-muted" role="status">
          {status}
        </span>
        <button
          onClick={() => void close()}
          disabled={closing}
          title="Save and close"
          aria-label="Save and close"
          className="grid h-8 w-8 place-items-center rounded text-text-muted hover:bg-bg-card-hover hover:text-text-heading disabled:opacity-50"
        >
          <i className={`fa-solid ${closing ? "fa-circle-notch fa-spin" : "fa-xmark"}`} />
        </button>
      </header>
      {conflict && (
        <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-2 text-sm text-warning" role="alert">
          This file changed elsewhere, so your latest edits were not saved.
          {hasCopy && (
            <button className="underline" onClick={() => copyRef.current()}>
              Download your copy
            </button>
          )}
          <button
            className="underline"
            onClick={() => {
              if (confirm("Reload the latest version and discard your unsaved edits?")) setEpoch((v) => v + 1);
            }}
          >
            Reload latest
          </button>
        </div>
      )}
      {error && (
        <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-2 text-sm text-danger" role="alert">
          {error}
          {hasCopy ? (
            <>
              <button className="underline" onClick={() => retryRef.current()}>
                Retry save
              </button>
              <button className="underline" onClick={() => copyRef.current()}>
                Download your copy
              </button>
            </>
          ) : (
            <button className="underline" onClick={() => setEpoch((v) => v + 1)}>
              Reload editor
            </button>
          )}
        </div>
      )}
      <div className="relative flex min-h-0 flex-1" aria-busy={!loaded && !error}>
        {!loaded && !error && (
          <div className="absolute inset-0 z-10 grid place-items-center text-text-muted">
            <i className="fa-solid fa-circle-notch fa-spin text-xl" />
          </div>
        )}
        <iframe
          key={`${docId}:${epoch}`}
          ref={frame}
          title={`Edit ${name} in ${LABEL[editor]}`}
          src={`/vendor/${editor}/index.html?host=parent&blank&theme=${initialTheme}`}
          onLoad={sendTheme}
          className="min-h-0 w-full flex-1 border-0"
          style={{ opacity: loaded ? 1 : 0, pointerEvents: loaded ? "auto" : "none" }}
        />
      </div>
    </div>,
    document.body,
  );
}
